import type {
  GetPlansQuery,
  PlanPeriod,
  PlanTier,
} from '@frameio/graphql-client/generated';
import { enterpriseMountedStorageLimitBytes } from '@frameio/shared/plans/constants';
import { formatBytes } from '@frameio/utils/formatters';
import { theme } from '@frameio/vapor';
import TEXT from './components/generated/TEXT/PlanConstants';

type PlanFeatures = {
  features: string[];
  background: string;
};

type PlanFeatureCards = {
  [key in 'FREE' | 'PRO' | 'TEAM' | 'ENTERPRISE']: PlanFeatures;
} & {
  [key: string]: PlanFeatures | undefined;
};

const enterpriseMountedStorageLimit = formatBytes(
  enterpriseMountedStorageLimitBytes
);

TEXT.DEFINE(
  {
    /** Feature text for up to X members on a plan. Use {memberCount} for the number of members */
    upToMembers: (memberCount: number): string =>
      `Up to ${memberCount} Members`,
    /** Feature text for storage display. Use {storage} for formatted storage amount */
    storage: (storage: string): string => `${storage} Storage`,
    /** Feature text for storage with per-user increment. Use {baseStorage} for base amount, {additionalStorage} for per-user amount */
    storageWithIncrement: (
      baseStorage: string,
      additionalStorage: string
    ): string =>
      `${baseStorage} Included + ${additionalStorage} per additional Member`,
    /** Feature text for up to X projects. Use {projectCount} for the number of projects */
    upToProjects: (projectCount: number): string =>
      `Up to ${projectCount} projects`,
    /** Feature text for Camera to Cloud */
    cameraToCloud: 'Camera to Cloud',
    /** Feature text for Transcription and captions */
    transcription: 'Transcription and captions',
    /** Feature text for unlimited projects */
    unlimitedProjects: 'Unlimited Projects',
    /** Feature text for custom-branded shares */
    customBrandedShares: 'Custom-Branded Shares',
    /** Feature text for passphrase-protected shares */
    passphraseProtectedShares: 'Passphrase-Protected Shares',
    /** Feature text for share link expiration */
    shareLinkExpiration: 'Share Link Expiration',
    /** Feature text for share permissions */
    sharePermissions: 'Share Permissions',
    /** Feature text for comment attachments */
    commentAttachments: 'Comment Attachments',
    /** Feature text for restricted projects */
    restrictedProjects: 'Restricted Projects',
    /** Feature text for restricted folders */
    restrictedFolders: 'Restricted Folders',
    /** Feature text for internal comments */
    internalComments: 'Internal Comments',
    /** Feature text for custom members and storage */
    customMembersStorage: 'Custom Members & Storage',
    /** Feature text for single sign-on */
    singleSignOn: 'Single Sign-On',
    /** Feature text for multiple workspaces */
    multipleWorkspaces: 'Multiple Workspaces',
    /** Feature text for secure sharing */
    secureSharing: 'Secure Sharing',
    /** Feature text for session-based watermarking */
    sessionBasedWatermarking: 'Session-Based Watermarking',
    /** Feature text for forensic watermarking */
    forensicWatermarking: 'Forensic Watermarking',
    /** Feature text for asset lifecycle management */
    assetLifecycleManagement: 'Asset Lifecycle Management',
    /** Feature text for Digital Rights Management (DRM) */
    digitalRightsManagement: 'Digital Rights Management (DRM)',
    /** Feature text for mounted storage. Use {storage} for formatted storage amount */
    mountedStorage: (storage: string): string => `${storage} Mounted Storage`,
    /** Feature text for mounted storage included (enterprise) */
    mountedStorageIncluded: `${enterpriseMountedStorageLimit} Mounted Storage`,
    /** Feature text for Storage Connect */
    storageConnect: 'Storage Connect',
    /** Feature text for priority in-app support */
    priorityInAppSupport: 'Priority In-App Support',
    /** Feature text for dedicated account team */
    dedicatedAccountTeam: 'Dedicated Account Team',
    /** Billing period labels */
    periods: {
      /** Monthly billing period abbreviation */
      monthly: 'mo.',
      /** Semi-annual billing period label */
      semiannually: 'six months',
      /** Yearly billing period abbreviation */
      yearly: 'yr.',
    },
  },
  { enterpriseMountedStorageLimit }
);

export const planFeaturesForCards = (
  plans: NonNullable<GetPlansQuery['account']>['plans'],
  isMountableStorageEnabled = false
): PlanFeatureCards => {
  // Find plans by tier to extract their values
  const freePlan = plans?.find((plan) => plan.tier === 'FREE');
  const proPlan = plans?.find((plan) => plan.tier === 'PRO');
  const teamPlan = plans?.find((plan) => plan.tier === 'TEAM');

  // Format storage text based on whether there's additional per-user storage
  const getStorageText = (plan: typeof proPlan) => {
    const baseStorage = formatBytes(plan?.activeStorageLimit);

    if (plan?.perUserStorageIncrement && plan.perUserStorageIncrement > 0) {
      const additionalStorage = formatBytes(plan.perUserStorageIncrement);
      return TEXT.storageWithIncrement(baseStorage, additionalStorage);
    }

    return TEXT.storage(baseStorage);
  };

  const getMountedStorageText = (plan: typeof freePlan) => {
    const mountedStorageText = TEXT.mountedStorage(
      formatBytes(plan?.mountedStorageLimit)
    );
    return plan?.tier === 'TEAM'
      ? `${mountedStorageText}†`
      : mountedStorageText;
  };

  return {
    FREE: {
      features: [
        TEXT.upToMembers(freePlan?.userMax ?? 0),
        getStorageText(freePlan),
        ...(isMountableStorageEnabled ? [getMountedStorageText(freePlan)] : []),
        TEXT.upToProjects(freePlan?.projectLimit ?? 0),
        TEXT.cameraToCloud,
        TEXT.transcription,
      ],
      background: theme.colors.components.pricing.background.free,
    },
    PRO: {
      features: [
        TEXT.upToMembers(proPlan?.userMax ?? 0),
        getStorageText(proPlan),
        ...(isMountableStorageEnabled ? [getMountedStorageText(proPlan)] : []),
        TEXT.unlimitedProjects,
        TEXT.customBrandedShares,
        TEXT.passphraseProtectedShares,
        TEXT.shareLinkExpiration,
        TEXT.sharePermissions,
        TEXT.commentAttachments,
      ],
      background: theme.colors.components.pricing.background.pro,
    },
    TEAM: {
      features: [
        TEXT.upToMembers(teamPlan?.userMax ?? 0),
        getStorageText(teamPlan),
        ...(isMountableStorageEnabled ? [getMountedStorageText(teamPlan)] : []),
        TEXT.restrictedProjects,
        TEXT.restrictedFolders,
        TEXT.internalComments,
      ],
      background: theme.colors.components.pricing.background.team,
    },
    ENTERPRISE: {
      features: [
        TEXT.customMembersStorage,
        ...(isMountableStorageEnabled ? [TEXT.mountedStorageIncluded] : []),
        TEXT.singleSignOn,
        TEXT.multipleWorkspaces,
        TEXT.secureSharing,
        TEXT.sessionBasedWatermarking,
        TEXT.forensicWatermarking,
        TEXT.assetLifecycleManagement,
        TEXT.digitalRightsManagement,
        TEXT.storageConnect,
        TEXT.priorityInAppSupport,
        TEXT.dedicatedAccountTeam,
      ],
      background: theme.colors.components.pricing.background.enterprise,
    },
  };
};

/** Dummy mock plan for enterprise. */
export const ENTERPRISE_PLAN = {
  activeStorageLimit: null,
  archivedStorageLimit: null,
  cost: null,
  defaultPlan: null,
  eligibility: null,
  enterprise: true,
  id: 'need-to-get-id',
  name: 'plan-name',
  paymentMethod: 'STRIPE',
  period: 'MONTHLY' as PlanPeriod,
  projectLimit: null,
  tier: 'ENTERPRISE' as PlanTier,
  title: 'Enterprise',
  userLimit: null,
  userMax: null,
  version: null,
  workspaceLimit: null,
  __typename: 'Plan',
};

export const getPeriodText = () => ({
  MONTHLY: TEXT.periods.monthly,
  SEMIANNUALLY: TEXT.periods.semiannually,
  YEARLY: TEXT.periods.yearly,
});
